import { AlertTriangle, CheckCircle2, Copy, Download, GraduationCap, LayoutDashboard, Link2, LogOut, Network, Plug, Users } from 'lucide-react'
import QRCode from 'qrcode'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAggregatorAccountType, getAggregatorToken, setAggregatorToken } from '../../api/client'
import { aggregatorPortalApi, type AggregatorPeopleKind } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { AggregatorPortalProfile } from '../../types/api'
import { BulkImportPanel, Column, type BulkImportPanelConfig } from '../admin/BulkImportPage'
import { NetworkOverview, PeopleSection, TabButton, Tile } from './portalShared'
import { DpgTab } from './DpgTab'
import { UploadInvitations } from './UploadInvitations'
import { slug } from './portalUtils'

type Tab = 'overview' | AggregatorPeopleKind | 'onboarding' | 'dpg'

const CATEGORY_HINT =
  'academics, competitive_exams, music, sports, arts, skills, ai, languages, school_admission, higher_education or internships'

/** The two uploads differ only by their columns and what the coordinator should know first. */
const IMPORT: Record<AggregatorPeopleKind, BulkImportPanelConfig> = {
  students: {
    noun: 'learner',
    nounPlural: 'learners',
    templateFilename: 'yellow-dot-learners.csv',
    columns: (
      <>
        <Column name="full_name" required>the learner&apos;s name</Column>
        <Column name="email" required>their own email - each learner needs a different one</Column>
        <Column name="phone" required>ten digits, +91 and spaces are fine</Column>
        <Column name="gender" required>MALE, FEMALE, OTHER, PREFER_NOT_TO_SAY</Column>
        <Column name="address">the full street address - optional</Column>
        <Column name="city" required>e.g. Delhi</Column>
        <Column name="state" required>e.g. Delhi</Column>
        <Column name="pincode" required>six digits, used to place them on the map</Column>
        <Column name="dob" required>YYYY-MM-DD, e.g. 2008-04-15</Column>
        <Column name="category" required>what they are looking for - {CATEGORY_HINT}</Column>
        <Column name="sub_category">the specific item within that category - optional</Column>
      </>
    ),
    notice:
      'Each learner gets a YellowDots account linked to you. Learners aged 18 and over are also published ' +
      'to the Signals network under your name and tracked on your Overview. A learner under 18 gets a ' +
      'YellowDots account only - the network does not take minors this way - and cannot connect to anyone ' +
      'until a parent approves.',
    upload: (file, dryRun, onLine) => aggregatorPortalApi.importPeople('students', file, dryRun, onLine),
    downloadTemplate: () => aggregatorPortalApi.importTemplate('students'),
  },
  tutors: {
    noun: 'tutor',
    nounPlural: 'tutors',
    templateFilename: 'yellow-dot-tutors.csv',
    columns: (
      <>
        <Column name="full_name" required>the tutor&apos;s name</Column>
        <Column name="email" required>their own email</Column>
        <Column name="phone" required>ten digits, +91 and spaces are fine</Column>
        <Column name="gender" required>MALE, FEMALE, OTHER, PREFER_NOT_TO_SAY</Column>
        <Column name="address">the full street address - optional</Column>
        <Column name="city" required>e.g. Delhi</Column>
        <Column name="state" required>e.g. Delhi</Column>
        <Column name="pincode" required>six digits, used to place them on the map</Column>
        <Column name="category" required>what they teach - {CATEGORY_HINT}</Column>
        <Column name="sub_category">the specific item within that category - optional</Column>
      </>
    ),
    notice:
      'Each tutor gets a YellowDots account linked to you, with the service the file names, and is published ' +
      'to the Signals network under your name. Uploading does not verify them: the YellowDots team reviews ' +
      'each tutor before they show as Verified to learners.',
    upload: (file, dryRun, onLine) => aggregatorPortalApi.importPeople('tutors', file, dryRun, onLine),
    downloadTemplate: () => aggregatorPortalApi.importTemplate('tutors'),
  },
}

/** Which uploads a coordinator gets: the one its "Aggregator Type" names, or both for an older account. */
function kindsFor(me: AggregatorPortalProfile): AggregatorPeopleKind[] {
  if (me.audience === 'STUDENTS') return ['students']
  if (me.audience === 'TUTORS') return ['tutors']
  return ['students', 'tutors']
}

/**
 * A coordinator's dashboard. Everything on it is for the kind of person the coordinator registered
 * to onboard - learners (Seekers) or tutors (Providers): its bulk upload, its list, its onboarding link
 * and QR, and the Signals network's tracking of the people it brought in.
 */
export function AggregatorPortalPage() {
  const navigate = useNavigate()
  // Back from the aggregator-dpg sign-in (?dpg=...), open its tab to say how it went.
  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(window.location.search).has('dpg') ? 'dpg' : 'overview',
  )
  const [me, setMe] = useState<AggregatorPortalProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const signOut = useCallback(() => {
    setAggregatorToken(null)
    navigate('/aggregator-portal/login', { replace: true })
  }, [navigate])

  const onError = useCallback(
    (err: unknown, fallback: string): string | null => {
      if (err instanceof ApiError && err.status === 401) {
        signOut()
        return null
      }
      return apiErrorMessage(err, fallback)
    },
    [signOut],
  )

  const loadMe = useCallback(async () => {
    try {
      setMe(await aggregatorPortalApi.me())
      setError(null)
    } catch (err) {
      setError(onError(err, 'Could not load your portal'))
    }
  }, [onError])

  useEffect(() => {
    if (getAggregatorToken() && getAggregatorAccountType() === 'COORDINATOR') void loadMe()
  }, [loadMe])

  const loadNetwork = useCallback(
    (params: { page: number; limit: number; status?: string }) => aggregatorPortalApi.networkDashboard(params),
    [],
  )
  const loadStudents = useCallback(() => aggregatorPortalApi.people('students'), [])
  const loadTutors = useCallback(() => aggregatorPortalApi.people('tutors'), [])

  if (!getAggregatorToken()) return <Navigate to="/aggregator-portal/login" replace />
  if (getAggregatorAccountType() === 'ORG') return <Navigate to="/aggregator-portal/org" replace />

  const kinds = me ? kindsFor(me) : []
  const audienceLabel = me?.audience === 'STUDENTS' ? 'Seekers (students)' : me?.audience === 'TUTORS' ? 'Providers (tutors)' : null

  return (
    <div className="min-h-screen bg-ink-100">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <div className="flex items-center gap-2">
              <Network size={18} className="text-sun-600" />
              <span className="font-bold text-ink-900">{me?.name ?? 'Coordinator'}</span>
              {audienceLabel && <span className="rounded-full bg-sun-400/20 px-2 py-0.5 text-xs font-semibold text-ink-800">{audienceLabel}</span>}
            </div>
            {me?.orgName && <div className="text-xs text-ink-500">Coordinator for {me.orgName}</div>}
          </div>
          <nav className="flex flex-wrap items-center gap-1">
            <TabButton icon={<LayoutDashboard size={14} />} label="Overview" current={tab === 'overview'} onClick={() => setTab('overview')} />
            {kinds.includes('students') && (
              <TabButton icon={<Users size={14} />} label="Students" current={tab === 'students'} onClick={() => setTab('students')} />
            )}
            {kinds.includes('tutors') && (
              <TabButton icon={<GraduationCap size={14} />} label="Tutors" current={tab === 'tutors'} onClick={() => setTab('tutors')} />
            )}
            <TabButton icon={<Link2 size={14} />} label="Invite link & QR" current={tab === 'onboarding'} onClick={() => setTab('onboarding')} />
            <TabButton icon={<Plug size={14} />} label="Aggregator DPG" current={tab === 'dpg'} onClick={() => setTab('dpg')} />
            <button onClick={signOut} className="btn-ghost gap-1.5">
              <LogOut size={14} /> Sign out
            </button>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <ErrorMessage message={error} />
        {me && <StatusBanner me={me} />}

        {me && tab === 'overview' && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              {kinds.includes('students') && <Tile icon={<Users size={16} />} label="Students brought in" value={me.studentsOnboarded} />}
              {kinds.includes('tutors') && <Tile icon={<GraduationCap size={16} />} label="Tutors brought in" value={me.tutorsOnboarded} />}
              <Tile icon={<CheckCircle2 size={16} />} label="Signals network" value={me.onNetwork ? 'Connected' : 'Not yet'} />
            </div>
            <NetworkOverview
              load={loadNetwork}
              exportCsv={(status) => aggregatorPortalApi.networkExport(status)}
              exportName={me.name}
              onError={onError}
              title="Your people on the Signals network"
            />
          </>
        )}

        {me && (tab === 'students' || tab === 'tutors') && (
          <div className="space-y-5">
            <section>
              <h2 className="text-lg font-bold text-ink-900">Bulk upload {tab}</h2>
              <p className="text-sm text-ink-500">
                Each row becomes a YellowDots account linked to you. Start from the template - the column names are
                matched exactly. A .csv, .xls or .xlsx all work.
              </p>
              {me.approvalStatus === 'SUSPENDED' ? (
                <p className="mt-3 text-sm text-red-700">Uploads are switched off while this account is suspended.</p>
              ) : (
                <BulkImportPanel
                  config={IMPORT[tab]}
                  inviteFrom="the Send invitations list below"
                  onFinished={() => {
                    setReloadKey((k) => k + 1)
                    void loadMe()
                  }}
                />
              )}
            </section>
            <UploadInvitations kind={tab} onError={onError} reloadKey={reloadKey} />
            <PeopleSection
              title={`Your ${tab}`}
              subtitle="Uploaded by you, or signed up through your invite link or QR. Most recent first."
              isTutors={tab === 'tutors'}
              load={tab === 'tutors' ? loadTutors : loadStudents}
              onError={onError}
              reloadKey={reloadKey}
            />
          </div>
        )}

        {me && tab === 'onboarding' && <OnboardingTab me={me} />}
        {me && tab === 'dpg' && <DpgTab />}
      </main>
    </div>
  )
}

function StatusBanner({ me }: { me: AggregatorPortalProfile }) {
  if (me.approvalStatus === 'SUSPENDED') {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <p>This account is suspended, so uploads and the invite link are switched off. Contact support.</p>
      </div>
    )
  }
  if (!me.onNetwork) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <p>
          Not connected to the Signals network yet. You can still bring people in - they are kept here and published
          under your name as soon as the connection is made.
        </p>
      </div>
    )
  }
  return null
}

function OnboardingTab({ me }: { me: AggregatorPortalProfile }) {
  const link = me.onboardingCode ? `${window.location.origin}/register?agg=${me.onboardingCode}` : null
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const who = me.audience === 'TUTORS' ? 'Tutors' : me.audience === 'STUDENTS' ? 'Learners' : 'Learners and tutors'

  useEffect(() => {
    if (!link) return
    QRCode.toDataURL(link, { width: 320, margin: 2 })
      .then(setQr)
      .catch(() => setQr(null))
  }, [link])

  if (!link) return <p className="text-sm text-ink-500">This account has no invite link.</p>

  async function copy() {
    try {
      await navigator.clipboard.writeText(link!)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <section className="card-pad">
      <h2 className="font-bold text-ink-900">Your invite link and QR code</h2>
      <p className="text-sm text-ink-500">
        Share the link, or print the QR at your centre. {who} who sign up through it create their own account - and
        give their own consent - and are linked to you, so they appear on your Overview and in your list.
      </p>
      <div className="mt-4 grid gap-6 md:grid-cols-[1fr_auto]">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-800">{link}</code>
            <button onClick={() => void copy()} className="btn-ghost gap-1.5">
              <Copy size={14} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-600">
            <li>The sign-up page says they are joining through {me.name}.</li>
            <li>It offers only the {who.toLowerCase()} option.</li>
            <li>Adults are published to the Signals network under your name once they add what they are looking for or offer.</li>
          </ul>
        </div>
        {qr && (
          <div className="text-center">
            <img src={qr} alt={`QR code for ${me.name}'s invite link`} className="mx-auto h-48 w-48 rounded-lg border border-ink-200 bg-white" />
            <a href={qr} download={`${slug(me.name)}-qr.png`} className="btn-ghost mt-2 gap-1.5">
              <Download size={14} /> Download QR
            </a>
          </div>
        )}
      </div>
    </section>
  )
}
